import type { ChipOwnProps } from '@mui/material';
import { groupBy, isEmpty, startCase } from 'lodash';

import type { PriorityTypes } from '@/components/PriorityChip/PriorityChip.types';
import { CASE_VERDICT_VARIANTS } from '@/features/CaseTable/CaseTable.constants';

import { TICKET_THREAT_RESOLUTIONS_MAP } from '../Ticket';
import type {
  AIOutputInvestigationCycle,
  Case,
  CaseAnalysis,
  CaseContinuedAnalysis
} from './Case.types';

// Maps the raw case data from the API to the Case type used in the frontend mainly needed to map ai output structure
export const mapCase = (caseData: Case) => {
  let research: CaseAnalysis = {
    reasoning: '',
    investigationCycles: []
  };

  const continuedResearch: CaseContinuedAnalysis[] = [];

  const mapInvestigationCycles = (cycles: AIOutputInvestigationCycle[]) => {
    return cycles?.map((cycle) => {
      const batches = Object.values(
        groupBy(
          cycle.tool_calls,
          (toolCall) => toolCall.tool_batch_id ?? toolCall.tool_call_id
        )
      );

      const batchData = batches.map((batch) => {
        return {
          batchReasoning: batch.map((toolCall) => toolCall.tool_call_reasoning),
          batchConclusion: batch[0].tool_call_conclusion,
          batchId: batch[0].tool_call_id,
          batchLabel: (
            batch[0].params.device_type ||
            batch[0].params.data_source_device_type ||
            ''
          ).toUpperCase()
        };
      });

      const toolCalls = cycle.tool_calls.map((toolCall) => ({
        id: toolCall.tool_call_id,
        reasoning: toolCall.tool_call_reasoning,
        conclusion: toolCall.tool_call_conclusion,
        response: toolCall.tool_call_response,
        name: toolCall.tool_name,
        label: (
          toolCall.params.device_type ||
          toolCall.params.data_source_device_type ||
          ''
        ).toUpperCase(),
        params: toolCall.params
      }));
      return {
        id: `${cycle.cycle_number}`,
        reasoning: cycle.analyst_reasoning,
        toolCalls,
        batchData
      };
    });
  };

  if (!isEmpty(caseData?.ai_output) && !caseData?.ai_output?.error) {
    const investigationCycles = mapInvestigationCycles(
      caseData.ai_output.investigation_cycles
    );
    research = {
      reasoning: caseData.ai_output.initial_reasoning,
      investigationCycles
    };
  }

  if (!isEmpty(caseData?.ai_output?.continued_analysis)) {
    caseData.ai_output?.continued_analysis.forEach((analysis) => {
      const investigationCycles = mapInvestigationCycles(
        analysis.investigation_cycles
      );
      continuedResearch.push({
        analysisGuidance: analysis.analysis_guidance,
        analysisStart: analysis.analysis_start || '',
        investigationCycles
      });
    });
  }

  return {
    ...caseData,
    customer_category:
      (caseData.ticket?.ticket_category?.name as string) ||
      caseData.ai_category ||
      '',
    scope_verdict:
      TICKET_THREAT_RESOLUTIONS_MAP[`${caseData.ticket?.resolution_id}`] || '',
    research,
    continuedResearch
  };
};

export const getColorByVerdict = (verdict: string) => {
  let color: ChipOwnProps['color'] = 'disabled';

  switch (true) {
    case /false positive|benign|dismiss_false_positive/i.test(verdict):
      color = CASE_VERDICT_VARIANTS['False Positive'];
      break;
    case verdict !== 'True Positive (Malicious - Successfully Blocked)' &&
      /true positive|escalated|threat mitigated|escalate_threat/i.test(verdict):
      color = CASE_VERDICT_VARIANTS['True Positive'];
      break;
    default:
      color = 'unknown';
      break;
  }

  return color;
};

export const caseAIStatusFormatter = (status: string) => {
  if (!status) return '';
  switch (status?.toLowerCase()) {
    case 'ai_processing':
      return 'AI Processing';
    case 'ai_error':
      return 'AI Error';
    case 'n/a':
      return 'N/A';
    default:
      return startCase(status);
  }
};

const normalizePriority = (
  value: string | null | undefined
): PriorityTypes | null => {
  if (!value || value.toLowerCase() === 'n/a') return null;
  const match = value.match(/^(P[1-5])/i);
  return match ? (match[1].toUpperCase() as PriorityTypes) : null;
};

export const getCasePriority = (
  aiPriority: string | null | undefined,
  socPriority: string | null | undefined
): PriorityTypes | null => {
  return normalizePriority(aiPriority) ?? normalizePriority(socPriority);
};
